import { useEffect } from "react";

export default function usePullToRefresh({ enabled = true, threshold = 80, onRefresh } = {}) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    let startY = 0;
    let currentY = 0;
    let pulling = false;
    const root = document.documentElement;
    const touchTarget = document.body || document.documentElement;
    const prevTouchAction = root.style.touchAction;
    const getScrollTop = () => {
      const scrollEl = document.scrollingElement || document.documentElement;
      return (
        window.pageYOffset ||
        window.scrollY ||
        scrollEl?.scrollTop ||
        document.body?.scrollTop ||
        0
      );
    };

    const setOffset = (value) => {
      root.style.setProperty("--ptr-offset", `${value}px`);
    };

    const onTouchStart = (event) => {
      if (getScrollTop() > 0) return;
      if (event.touches.length !== 1) return;
      startY = event.touches[0].clientY;
      currentY = startY;
      pulling = true;
      root.classList.add("ptr-pulling");
    };

    const onTouchMove = (event) => {
      if (!pulling) return;
      currentY = event.touches[0].clientY;
      const delta = currentY - startY;
      if (delta > 0) {
        if (event.cancelable && typeof event.preventDefault === "function") {
          event.preventDefault();
        }
        const dampened = Math.min(140, delta * 0.55);
        setOffset(dampened);
      } else {
        pulling = false;
        setOffset(0);
        root.classList.remove("ptr-pulling");
      }
    };

    const onTouchEnd = () => {
      if (!pulling) return;
      const delta = currentY - startY;
      pulling = false;
      setOffset(0);
      root.classList.remove("ptr-pulling");
      if (delta >= threshold && getScrollTop() === 0) {
        try {
          if (typeof window !== "undefined" && window.sessionStorage) {
            window.sessionStorage.setItem("ptrRefreshAt", String(Date.now()));
          }
        } catch (err) {
          // ignore storage errors
        }
        if (typeof onRefresh === "function") {
          Promise.resolve(onRefresh())
            .catch((err) => {
              console.warn("pull-to-refresh failed", err);
            });
        } else {
          window.location.reload();
        }
      }
    };

    const onPointerDown = (event) => {
      if (event.pointerType !== "touch") return;
      onTouchStart({ touches: [event] });
    };
    const onPointerMove = (event) => {
      if (event.pointerType !== "touch") return;
      onTouchMove({ touches: [event], cancelable: event.cancelable, preventDefault: () => event.preventDefault() });
    };
    const onPointerUp = (event) => {
      if (event.pointerType !== "touch") return;
      onTouchEnd();
    };

    root.style.touchAction = "pan-y";
    const optsStart = { passive: true, capture: true };
    const optsMove = { passive: false, capture: true };
    const optsEnd = { capture: true };
    touchTarget.addEventListener("touchstart", onTouchStart, optsStart);
    touchTarget.addEventListener("touchmove", onTouchMove, optsMove);
    touchTarget.addEventListener("touchend", onTouchEnd, optsEnd);
    touchTarget.addEventListener("touchcancel", onTouchEnd, optsEnd);
    window.addEventListener("pointerdown", onPointerDown, optsStart);
    window.addEventListener("pointermove", onPointerMove, optsMove);
    window.addEventListener("pointerup", onPointerUp, optsEnd);
    window.addEventListener("pointercancel", onPointerUp, optsEnd);

    return () => {
      root.style.touchAction = prevTouchAction;
      touchTarget.removeEventListener("touchstart", onTouchStart, optsStart);
      touchTarget.removeEventListener("touchmove", onTouchMove, optsMove);
      touchTarget.removeEventListener("touchend", onTouchEnd, optsEnd);
      touchTarget.removeEventListener("touchcancel", onTouchEnd, optsEnd);
      window.removeEventListener("pointerdown", onPointerDown, optsStart);
      window.removeEventListener("pointermove", onPointerMove, optsMove);
      window.removeEventListener("pointerup", onPointerUp, optsEnd);
      window.removeEventListener("pointercancel", onPointerUp, optsEnd);
    };
  }, [enabled, threshold, onRefresh]);
}
